import Button from '@rio-cloud/rio-uikit/Button';
import ContentLoader from '@rio-cloud/rio-uikit/ContentLoader';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import EmptyState from '@rio-cloud/rio-uikit/EmptyState';
import ErrorState from '@rio-cloud/rio-uikit/ErrorState';
import NoData from '@rio-cloud/rio-uikit/NoData';
import Rioglyph, { type IconType } from '@rio-cloud/rio-uikit/Rioglyph';
import Tag from '@rio-cloud/rio-uikit/Tag';
import TagList from '@rio-cloud/rio-uikit/TagList';
import Table, { TableBody, TableColumn, TableHeader, TableHeaderColumn, TableRow } from '@rio-cloud/rio-uikit/Table';
import { FormattedDate, useIntl } from 'react-intl';
import { type DeviceSharing, type DeviceSharingAccessRules, useGetDeviceSharingsQuery } from '../../../services/assetsApi';
import type { Asset, AssociatedDevice } from './types';

type Props = {
    asset: Asset;
    devices: AssociatedDevice[];
    onClose: () => void;
};

type SharingAccount = {
    id: string;
    name: string;
    sourceDeviceIds: string[];
    sharingStartedAt: string;
    accessRules: DeviceSharingAccessRules[];
};

type SharedDataCategory = {
    messageId:
        | 'intl-msg:sharing.driver-data'
        | 'intl-msg:sharing.asset-data'
        | 'intl-msg:sharing.position'
        | 'intl-msg:sharing.state'
        | 'intl-msg:sharing.maintenance';
};

type IconLabelProps = {
    icon: IconType;
    label: string;
};

const IconLabel = ({ icon, label }: IconLabelProps) => (
    <span className='display-inline-flex align-items-center gap-5'>
        <Rioglyph icon={icon} />
        {label}
    </span>
);

const getSharingAccounts = (assetSharings: DeviceSharing[]): SharingAccount[] => {
    const sharingAccounts = new Map<string, SharingAccount>();

    for (const sharing of assetSharings) {
        const existingSharingAccount = sharingAccounts.get(sharing.shared_account_id);

        if (existingSharingAccount) {
            if (!existingSharingAccount.sourceDeviceIds.includes(sharing.device_id)) {
                existingSharingAccount.sourceDeviceIds.push(sharing.device_id);
            }

            existingSharingAccount.accessRules.push(sharing.shared_access_rules ?? { driverstate_rules: [], assetstate_rules: [] });

            if (sharing.sharing_started_at && sharing.sharing_started_at < existingSharingAccount.sharingStartedAt) {
                existingSharingAccount.sharingStartedAt = sharing.sharing_started_at;
            }

            continue;
        }

        sharingAccounts.set(sharing.shared_account_id, {
            id: sharing.shared_account_id,
            name: sharing.shared_account_name ?? sharing.shared_account_id,
            sourceDeviceIds: [sharing.device_id],
            sharingStartedAt: sharing.sharing_started_at ?? '',
            accessRules: [sharing.shared_access_rules ?? { driverstate_rules: [], assetstate_rules: [] }],
        });
    }

    return Array.from(sharingAccounts.values());
};

const getSharedDataCategories = (accessRules: DeviceSharingAccessRules[]): SharedDataCategory[] => {
    const driverDataIsShared = accessRules.some(accessRule => accessRule.driverstate_rules.length);
    const assetStateRules = new Set(accessRules.flatMap(accessRule => accessRule.assetstate_rules));
    const assetDataIsShared = assetStateRules.has('all');
    const categories: SharedDataCategory[] = [];

    if (driverDataIsShared) {
        categories.push({ messageId: 'intl-msg:sharing.driver-data' });
    }

    if (assetDataIsShared) {
        categories.push({ messageId: 'intl-msg:sharing.asset-data' });
    } else {
        const supportedAssetDataRules = [
            'position',
            'state',
            'maintenance',
        ] as const;

        for (const rule of supportedAssetDataRules) {
            if (assetStateRules.has(rule)) {
                categories.push({ messageId: `intl-msg:sharing.${rule}` });
            }
        }
    }

    return categories;
};

export const AssetSharingsDialog = ({ asset, devices, onClose }: Props) => {
    const intl = useIntl();
    const { data: deviceSharings, isError, isLoading, refetch } = useGetDeviceSharingsQuery();
    const assetSharings = deviceSharings?.filter(sharing => sharing.asset_id === asset.id);
    const deviceIdentifications = new Map(devices.map(({ device }) => [device.id, device.identification]));
    const sharingAccounts = getSharingAccounts(assetSharings ?? []);

    const body = (() => {
        if (isLoading) {
            return <ContentLoader height={20} />;
        }

        if (isError) {
            return (
                <ErrorState
                    headline={intl.formatMessage({ id: 'intl-msg:sharing.error.headline' })}
                    message={intl.formatMessage({ id: 'intl-msg:sharing.error.message' })}
                    buttons={[
                        {
                            text: intl.formatMessage({ id: 'intl-msg:sharing.retry' }),
                            onClick: refetch,
                        },
                    ]}
                />
            );
        }

        if (sharingAccounts.length === 0) {
            return (
                <EmptyState
                    headline={intl.formatMessage({ id: 'intl-msg:sharing.empty.headline' })}
                    message={intl.formatMessage({ id: 'intl-msg:sharing.empty.message' })}
                />
            );
        }

        return (
            <Table rowKey='id' condensed responsiveBreakpoint={{list: 500}}>
                <TableHeader>
                    <TableHeaderColumn
                        columnKey='account'
                        label={intl.formatMessage({ id: 'intl-msg:sharing.account' })}
                        mobileLabel={intl.formatMessage({ id: 'intl-msg:sharing.account' })}
                    >
                        <IconLabel
                            icon='rioglyph-user-group'
                            label={intl.formatMessage({ id: 'intl-msg:sharing.account' })}
                        />
                    </TableHeaderColumn>
                    <TableHeaderColumn
                        columnKey='sourceData'
                        label={intl.formatMessage({ id: 'intl-msg:sharing.source-data' })}
                        mobileLabel={intl.formatMessage({ id: 'intl-msg:sharing.source-data' })}
                    >
                        <IconLabel
                            icon='rioglyph-truck'
                            label={intl.formatMessage({ id: 'intl-msg:sharing.source-data' })}
                        />
                    </TableHeaderColumn>
                    <TableHeaderColumn
                        columnKey='sharedSince'
                        label={intl.formatMessage({ id: 'intl-msg:sharing.shared-since' })}
                        mobileLabel={intl.formatMessage({ id: 'intl-msg:sharing.shared-since' })}
                    >
                        <IconLabel
                            icon='rioglyph-calendar'
                            label={intl.formatMessage({ id: 'intl-msg:sharing.shared-since' })}
                        />
                    </TableHeaderColumn>
                    <TableHeaderColumn
                        columnKey='sharedData'
                        label={intl.formatMessage({ id: 'intl-msg:sharing.shared-data' })}
                        mobileLabel={intl.formatMessage({ id: 'intl-msg:sharing.shared-data' })}
                    >
                        <IconLabel
                            icon='rioglyph-cloud'
                            label={intl.formatMessage({ id: 'intl-msg:sharing.shared-data' })}
                        />
                    </TableHeaderColumn>
                </TableHeader>
                <TableBody>
                    {sharingAccounts.map(sharingAccount => {
                        const sharedDataCategories = getSharedDataCategories(sharingAccount.accessRules);
                        const sourceDevices = sharingAccount.sourceDeviceIds.map(deviceId => ({
                            id: deviceId,
                            identification: deviceIdentifications.get(deviceId) ?? deviceId,
                        }));

                        return (
                            <TableRow key={sharingAccount.id} id={sharingAccount.id} noHover>
                                <TableColumn columnKey='account'><b>{sharingAccount.name}</b></TableColumn>
                                <TableColumn columnKey='sourceData'>
                                    <div className='display-flex flex-column gap-1'>
                                    {sourceDevices.length ? (
                                        sourceDevices.map(({ id, identification }) => (
                                            <span key={id}>
                                                <code>{identification}</code>
                                            </span>
                                        ))
                                    ) : (
                                        <NoData text={intl.formatMessage({ id: 'intl-msg:common-message.noData' })} />
                                    )}</div>
                                </TableColumn>
                                <TableColumn columnKey='sharedSince'>
                                    <FormattedDate
                                        value={sharingAccount.sharingStartedAt}
                                        year='numeric'
                                        month='2-digit'
                                        day='2-digit'
                                        hour='2-digit'
                                        minute='2-digit'
                                    />
                                </TableColumn>
                                <TableColumn columnKey='sharedData'>
                                    {sharedDataCategories.length ? (
                                        <TagList>
                                            {sharedDataCategories.map(({ messageId }) => (
                                                <Tag key={messageId} size='small' muted>
                                                    {intl.formatMessage({ id: messageId })}
                                                </Tag>
                                            ))}
                                        </TagList>
                                    ) : (
                                        <NoData text={intl.formatMessage({ id: 'intl-msg:common-message.noData' })} />
                                    )}
                                </TableColumn>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        );
    })();

    return (
        <Dialog
            show
            showCloseButton
            bsSize='lg'
            useOverflow
            title={intl.formatMessage({ id: 'intl-msg:sharing' })}
            subtitle={asset.name}
            body={body}
            footer={
                <Button onClick={onClose}>{intl.formatMessage({ id: 'intl-msg:sharing.close' })}</Button>
            }
            onClose={onClose}
        />
    );
};
